import { Card, EmptyState, Input } from '@ui/themed';
import { t } from 'i18next';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useDebouncedState } from '../../../hooks/use-debounced-state';
import { EXCLUDED_DEFAULT_SALES_AGENT_EMAIL } from '../constants';
import { useSearchCustomers } from '../hooks/use-search-customers';
import type { CustomerSearchQuery } from '../types/api';
import { CustomersTable, type CustomersTableProps } from './customers-table';

type CustomersSearchTableEmptyState = {
  title: string;
  subtitle?: string;
  imageType?: 'customerGroup' | 'priceList' | 'shippingMethod' | 'errorState' | 'salesAgent';
  children?: ReactNode;
};

type CustomersSearchTableProps = Omit<CustomersTableProps, 'pagination' | 'customers'> & {
  baseSearchRequest: CustomerSearchQuery;
  /** Shown when no customers match the search. Omit to show the table with no rows. */
  emptyState?: CustomersSearchTableEmptyState;
};

export function CustomersSearchTable({
  baseSearchRequest,
  emptyState,
  ...tableProps
}: CustomersSearchTableProps): JSX.Element {
  const [searchValue, setSearchValue] = useState(baseSearchRequest.search_term || '');

  const searchQuery = useMemo(() => {
    return {
      ...baseSearchRequest,
      ...(searchValue ? { search_term: searchValue } : {}),
    };
  }, [baseSearchRequest, searchValue]);

  const {
    data,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
    refetch,
    isError,
    isLoading,
    isFetching,
    isPlaceholderData,
    page,
    pagination,
  } = useSearchCustomers(searchQuery);

  const paginationCounts =
    pagination?.per_page && data?.length
      ? {
          first: pagination.per_page * (page - 1) + 1,
          last: pagination.per_page * (page - 1) + data.length,
          total: pagination.total,
        }
      : undefined;

  // Since the following account is created as a sales agent by default but not included in the
  // sales agent counts, and we need the empty state shown hide the account accept if searched for!
  const customers = searchValue
    ? data
    : data?.filter((x) => x.email !== EXCLUDED_DEFAULT_SALES_AGENT_EMAIL);

  const showEmptyState = emptyState && (customers?.length ?? 0) === 0 && !isLoading;

  return (
    <SparkSuspense
      onReload={refetch}
      isError={isError}
      errorMessage={t('features:customers.components.customers_search_table.load_error')}
      isLoading={isLoading}
    >
      {(searchValue.length > 0 || (customers && customers.length > 1)) && (
        <CustomerSearchBar onSearchChange={setSearchValue} />
      )}
      {showEmptyState ? (
        <Card>
          <EmptyState
            title={emptyState.title}
            subtitle={emptyState.subtitle}
            imageType={emptyState.imageType ?? 'customerGroup'}
          >
            {emptyState.children}
          </EmptyState>
        </Card>
      ) : (
        <CustomersTable
          customers={customers ?? []}
          pagination={{
            hasNext: !(isPlaceholderData || (!isPlaceholderData && isFetching)) && hasNextPage,
            hasPrevious:
              !(isPlaceholderData || (!isPlaceholderData && isFetching)) && hasPreviousPage,
            onNext: nextPage,
            onPrevious: previousPage,
            counts: paginationCounts,
          }}
          {...tableProps}
        />
      )}
    </SparkSuspense>
  );
}

export function CustomerSearchBar({
  onSearchChange,
  placeholder = t('features:customers.components.customers_search_table.search_bar.placeholder'),
}: {
  onSearchChange: Dispatch<SetStateAction<string>>;
  placeholder?: string;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedState(searchValue, 300);

  useEffect(() => {
    // The search is triggered only if the debounced value has at least 2 characters or is cleared
    // The openapi spec requires at least 2 characters for searching customers
    if (debouncedSearchValue.length >= 2 || debouncedSearchValue.length === 0) {
      onSearchChange(debouncedSearchValue);
    }
  }, [debouncedSearchValue, onSearchChange]);

  return (
    <div className="max-w-3xs">
      <Input
        icon="search"
        placeholder={placeholder}
        value={searchValue}
        onChange={setSearchValue}
      />
    </div>
  );
}
